/**
 * Search-as-you-type brand picker, used by the Unmapped Inbox to map a raw brand value to a
 * registry brand in one tap. Bottom sheet rather than a full page — mapping is a queue task
 * that happens many times per session, so it must not cost a navigation round-trip.
 */
import { useMemo, useState } from 'react';
import { Search, Check } from 'lucide-react';
import { BottomSheet } from '@/shared/ui';
import { haptic } from '@/shared/utils/telegram';
import type { RegistryBrand } from '@/shared/api/registry-api';

interface BrandPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  brands: RegistryBrand[];
  isLoading: boolean;
  onSelect: (brand: RegistryBrand) => void;
  pendingBrandId?: string;
}

export function BrandPickerSheet({
  isOpen,
  onClose,
  brands,
  isLoading,
  onSelect,
  pendingBrandId,
}: BrandPickerSheetProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query) return brands;
    const q = query.toLowerCase();
    return brands.filter(
      (b) => b.displayName.toLowerCase().includes(q) || b.slug.toLowerCase().includes(q),
    );
  }, [brands, query]);

  const handleSelect = (brand: RegistryBrand) => {
    haptic.impact('light');
    onSelect(brand);
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Map to brand">
      <div className="flex flex-col gap-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search size={14} className="text-[var(--tg-theme-hint-color)]" />
          </div>
          <input
            type="text"
            autoFocus
            placeholder="Search brands..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-11 pl-10 pr-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20 placeholder:opacity-50"
          />
        </div>

        {isLoading && (
          <p className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase text-center py-6">
            Loading brands...
          </p>
        )}

        {!isLoading && filtered.length === 0 && (
          <p className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase text-center py-6">
            No brands found
          </p>
        )}

        <div className="flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto">
          {filtered.map((brand) => {
            const isPending = pendingBrandId === brand.id;
            return (
              <button
                key={brand.id}
                type="button"
                disabled={isPending}
                onClick={() => handleSelect(brand)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-left active:scale-[0.98] transition-all disabled:opacity-50"
              >
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[var(--tg-theme-text-color)] truncate">
                    {brand.displayName}
                  </p>
                  {brand.slug.toLowerCase() !== brand.displayName.toLowerCase() && (
                    <p className="text-[11px] text-[var(--tg-theme-hint-color)] font-medium truncate">
                      {brand.slug}
                    </p>
                  )}
                </div>
                {isPending ? (
                  <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-[var(--tg-theme-link-color)] border-t-transparent" />
                ) : (
                  <Check
                    size={16}
                    className="flex-shrink-0 text-[var(--tg-theme-hint-color)] opacity-0"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
}
